import { AcaoNotaFiscalSaidaEnum } from '@shared/enums/vendas/acao-nota-fiscal-saida.enum';
import { AcaoNotaFiscalSaidaStruct } from '@shared/services/structs/repo-dfe/acao-nota-fiscal-saida.struct';
import { NotaFiscalSaidaListStruct } from '@shared/services/structs/repo-dfe/nota-fiscal-saida-list.struct';
import { StatusNFeSaidaEnum } from '@shared/enums/repo-dfe/status-nfe-saida.enum';

/**
 * Monta o menu de contexto da nota fiscal de saída conforme o status da nota. A primeira ação é a
 * principal: editar a nota fiscal no status editável (`isStatusEditavelNFeSaida`, abre o documento) ou visualizar nos
 * demais (abre a visualização). Ver histórico, download do espelho e alterar tags valem para qualquer
 * status (tag não entra no XML). Clonar vale para qualquer status e fica desabilitado sem a permissão de
 * incluir nota fiscal (`podeInserir`), como na venda. Excluir só para nota que nunca foi autorizada
 * (`motivoExclusaoIndisponivelNFeSaida`); a falta da permissão de excluir é recusada pela API. Transmitir só aparece
 * com a permissão de transmitir (`podeTransmitir`) e na nota que pode ir à SEFAZ: a mesma regra de status do documento
 * editável (Aguardando transmissão, Rejeitada, ou Em transmissão com transmissão não realizada). Lançar e excluir o
 * financeiro seguem `acaoFinanceiraNotaFiscalSaida`.
 */
export function acoesNotaFiscalSaida(
  nota: NotaFiscalSaidaListStruct,
  podeInserir: boolean,
  podeTransmitir: boolean,
  podeExcluirFinanceiro: boolean,
): AcaoNotaFiscalSaidaStruct[] {
  const isEditavel = isStatusEditavelNFeSaida(nota.idStatusNFeSaida, nota.possuiTransmissaoNaoRealizada);
  const abrirNotaFiscal: LinhaAcao = isEditavel
    ? [AcaoNotaFiscalSaidaEnum.EditarNotaFiscal, 'Editar nota fiscal', 'edit', true, '']
    : [AcaoNotaFiscalSaidaEnum.VisualizarNotaFiscal, 'Visualizar nota fiscal', 'visibility', true, ''];
  const transmitir: LinhaAcao = [AcaoNotaFiscalSaidaEnum.Transmitir, 'Transmitir', 'send', true, ''];
  const verHistorico: LinhaAcao = [AcaoNotaFiscalSaidaEnum.VerHistorico, 'Ver histórico', 'history', true, ''];
  const downloadEspelho: LinhaAcao = [
    AcaoNotaFiscalSaidaEnum.DownloadEspelho,
    'Download espelho da NF',
    'file_download',
    true,
    '',
  ];
  const alterarTags: LinhaAcao = [AcaoNotaFiscalSaidaEnum.AlterarTags, 'Alterar tags', 'label', true, ''];
  const clonarNota: LinhaAcao = [
    AcaoNotaFiscalSaidaEnum.ClonarNota,
    'Clonar nota',
    'content_copy',
    podeInserir,
    'Sem permissão para incluir nota fiscal.',
  ];
  const motivoExclusao = motivoExclusaoIndisponivelNFeSaida(nota);
  const excluirNota: LinhaAcao = [AcaoNotaFiscalSaidaEnum.ExcluirNota, 'Excluir nota', 'delete', !motivoExclusao, motivoExclusao];

  const isTransmitirVisivel = podeTransmitir && isEditavel;
  const acaoFinanceira = linhaAcaoFinanceira(nota.isFinanceiroLancado, nota.idStatusNFeSaida, podeInserir, podeExcluirFinanceiro);

  return montar([
    abrirNotaFiscal,
    ...(isTransmitirVisivel ? [transmitir] : []),
    ...(acaoFinanceira ? [acaoFinanceira] : []),
    verHistorico,
    downloadEspelho,
    alterarTags,
    clonarNota,
    excluirNota,
  ]);
}

/**
 * Lançar ou excluir o financeiro da nota (T19; regras da NF-e de entrada). Com o financeiro lançado, "Excluir
 * financeiro" em qualquer status, com a permissão de excluir o financeiro da nota. Sem ele, "Lançar financeiro" só na
 * nota autorizada, com a permissão de incluir nota fiscal. Sem a permissão a ação fica desabilitada com o motivo; fora
 * dessas situações não há ação (nulo). Usada pela listagem e pela visualização; o documento só abre a nota editável.
 */
export function acaoFinanceiraNotaFiscalSaida(
  isFinanceiroLancado: boolean,
  idStatusNFeSaida: number | null | undefined,
  podeInserir: boolean,
  podeExcluirFinanceiro: boolean,
): AcaoNotaFiscalSaidaStruct | null {
  const linha = linhaAcaoFinanceira(isFinanceiroLancado, idStatusNFeSaida, podeInserir, podeExcluirFinanceiro);
  return linha ? montar([linha])[0] : null;
}

function linhaAcaoFinanceira(
  isFinanceiroLancado: boolean,
  idStatusNFeSaida: number | null | undefined,
  podeInserir: boolean,
  podeExcluirFinanceiro: boolean,
): LinhaAcao | null {
  // A recusa por parcela recebida, agrupada ou em período fechado vem do Finanças no diálogo de excluir.
  if (isFinanceiroLancado) {
    return [
      AcaoNotaFiscalSaidaEnum.ExcluirFinanceiro,
      'Excluir financeiro',
      'payments',
      podeExcluirFinanceiro,
      'Você não tem permissão para excluir o financeiro desta nota.',
    ];
  }

  if (idStatusNFeSaida !== StatusNFeSaidaEnum.Autorizada) return null;

  return [
    AcaoNotaFiscalSaidaEnum.LancarFinanceiro,
    'Lançar financeiro',
    'payments',
    podeInserir,
    'Você não tem permissão para lançar o financeiro desta nota.',
  ];
}

/**
 * Aguardando transmissão e Rejeitada abrem no documento (editável); Autorizada, Denegada, Cancelada e Em transmissão
 * abrem na visualização (D38). A nota Em transmissão com a divergência de transmissão não realizada (a transmissão
 * falhou antes da SEFAZ) também abre no documento: `isTransmissaoNaoRealizada` diz se a nota a tem (no documento, pela
 * `listaDivergencia`; na listagem, por `possuiTransmissaoNaoRealizada`).
 */
export function isStatusEditavelNFeSaida(
  idStatusNFeSaida: number | null | undefined,
  isTransmissaoNaoRealizada = false,
): boolean {
  return idStatusNFeSaida === StatusNFeSaidaEnum.AguardandoTransmissao
    || idStatusNFeSaida === StatusNFeSaidaEnum.Rejeitada
    || (idStatusNFeSaida === StatusNFeSaidaEnum.EmTransmissao && isTransmissaoNaoRealizada);
}

/**
 * Por que a nota não pode ser excluída; vazio quando pode. Só a nota que nunca foi autorizada
 * (Aguardando transmissão, Rejeitada ou Em transmissão com transmissão não realizada) e sem estoque e financeiro
 * lançados sai do Workspace.
 */
export function motivoExclusaoIndisponivelNFeSaida(nota: NotaFiscalSaidaListStruct): string {
  switch (nota.idStatusNFeSaida) {
    case StatusNFeSaidaEnum.EmTransmissao:
      if (nota.possuiTransmissaoNaoRealizada) break;
      return 'Nota enviada para transmissão não pode ser excluída enquanto aguarda o retorno da SEFAZ.';
    case StatusNFeSaidaEnum.Autorizada:
      return 'Nota autorizada só pode ser cancelada.';
    case StatusNFeSaidaEnum.Cancelada:
      return 'Nota cancelada fica arquivada como documento fiscal.';
    case StatusNFeSaidaEnum.Denegada:
      return 'Nota denegada fica arquivada como documento fiscal.';
  }

  if (nota.isEstoqueLancado || nota.isFinanceiroLancado) return 'Exclua o estoque e o financeiro lançados antes de excluir a nota.';

  return '';
}

type LinhaAcao = [AcaoNotaFiscalSaidaEnum, string, string, boolean, string];

function montar(linhas: LinhaAcao[]): AcaoNotaFiscalSaidaStruct[] {
  return linhas.map(([acao, rotulo, icone, isDisponivel, motivoIndisponivel]) => {
    const item = new AcaoNotaFiscalSaidaStruct();
    item.acao = acao;
    item.rotulo = rotulo;
    item.icone = icone;
    item.isDisponivel = isDisponivel;
    item.motivoIndisponivel = isDisponivel ? '' : motivoIndisponivel;
    return item;
  });
}
